import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

export type UserPayoutStatus = 'awaiting_confirmation' | 'requested' | 'pending' | 'completed' | 'cancelled';

export type PayoutToken = 'USDT' | 'USDC';

export interface UserPayout {
  id: number;
  status: UserPayoutStatus;
  target_type: 'web3';
  web3_token?: PayoutToken;
  amount: number;
  fee?: number;
  amount_sent?: number;
  net_amount: number;
  subject?: string;
  description?: string;
  requested_at?: string;
  start_pending_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  // Masked wallet for list view
  web3_target_wallet_display?: string;
  // Full wallet for detail view
  web3_target_wallet?: string;
  web3_tx_hash?: string;
  web3_confirmations?: number;
  web3_block_number?: number;
  web3_verified_at?: string;
}

export interface UserPayoutFilters {
  filter_status?: UserPayoutStatus;
  filter_created_at_start?: string;
  filter_created_at_end?: string;
  page?: number;
  per_page?: number;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface UserPayoutListResponse {
  payouts: UserPayout[];
  total_count: number;
  total_amount: number;
  total_fee: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface UserPayoutInfo {
  balance: number;
  total_balance: number;
  pending_payout_amount: number;
  fee_percentage: number;
  fee_fixed: number;
  minimum_amount: number;
  fee_formula: string;
  // EU/EEA stablecoin policy: europeans may only be paid out in USDC.
  european?: boolean;
  available_tokens?: PayoutToken[];
}

export interface FeeCalculation {
  amount: number;
  fee: number;
  amount_sent: number;
  fee_percentage: number;
  fee_fixed: number;
  minimum_amount: number;
}

export interface CreateUserPayoutRequest {
  amount: number;
  wallet_address: string;
  subject?: string;
  description?: string;
  // Settlement token to receive. USDT additionally requires non_eu_confirmation.
  token?: PayoutToken;
  non_eu_confirmation?: boolean;
}

export interface UserPayoutResponse {
  payout: UserPayout;
  message: string;
}

export interface CreatePayoutResponse {
  payout: UserPayout;
  status: 'awaiting_confirmation';
  confirmation_code_sent: boolean;
  expires_at: string;
  message: string;
}

export interface ConfirmPayoutResponse {
  payout: UserPayout;
  message: string;
}

export interface ResendCodeResponse {
  confirmation_code_sent: boolean;
  expires_at: string;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class UserPayoutService {
  constructor(private http: HttpClient) {}

  /**
   * Get user's payout info (balance, wallet, fee structure)
   */
  getPayoutInfo(): Observable<UserPayoutInfo> {
    return this.http.get<UserPayoutInfo>('/user/payouts/info');
  }

  /**
   * Calculate fee for a given amount
   */
  calculateFee(amount: number): Observable<FeeCalculation> {
    return this.http.get<FeeCalculation>('/user/payouts/calculate_fee', {
      params: { amount: amount.toString() }
    });
  }

  /**
   * Get list of user's own payouts
   */
  getPayouts(filters: UserPayoutFilters = {}): Observable<UserPayoutListResponse> {
    let params = new HttpParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    return this.http.get<UserPayoutListResponse>('/user/payouts', { params });
  }

  /**
   * Get a single payout detail
   */
  getPayout(id: number): Observable<{ payout: UserPayout }> {
    return this.http.get<{ payout: UserPayout }>(`/user/payouts/${id}`);
  }

  /**
   * Create a new payout request (initiates confirmation flow)
   * Returns payout in 'awaiting_confirmation' status with confirmation code sent to email
   */
  createPayout(data: CreateUserPayoutRequest): Observable<CreatePayoutResponse> {
    return this.http.post<CreatePayoutResponse>('/user/payouts', data);
  }

  /**
   * Confirm a payout with the 6-digit confirmation code
   */
  confirmPayout(id: number, confirmationCode: string): Observable<ConfirmPayoutResponse> {
    return this.http.post<ConfirmPayoutResponse>(`/user/payouts/${id}/confirm`, {
      confirmation_code: confirmationCode
    });
  }

  /**
   * Resend confirmation code for a payout
   */
  resendConfirmationCode(id: number): Observable<ResendCodeResponse> {
    return this.http.post<ResendCodeResponse>(`/user/payouts/${id}/resend_code`, {});
  }

  /**
   * Cancel a payout request (if status is 'requested' or 'awaiting_confirmation')
   */
  cancelPayout(id: number): Observable<UserPayoutResponse> {
    return this.http.post<UserPayoutResponse>(`/user/payouts/${id}/cancel`, {});
  }

  /**
   * Calculate fee locally (for instant UI feedback)
   * Fee = 4% + 5 USDT
   */
  calculateFeeLocal(amount: number): { fee: number; amountSent: number } {
    const fee = Math.round((amount * 0.04 + 5) * 100) / 100;
    const amountSent = Math.round((amount - fee) * 100) / 100;
    return { fee, amountSent: amountSent > 0 ? amountSent : 0 };
  }
}
